import { Dispatch, useCallback, useState } from 'react';

import { format, parse } from 'date-fns';
import { isArray } from 'lodash-es';

import { AppointmentSearch, Timeslot } from '@/features/book-appointment/booking.types';
import { CELEBRUS_CALENDAR_HEADER_CLASSNAMES } from '@/lib/constants';
import { cn } from '@/lib/utils';

import styles from '../../../booking.module.css';
import { HOLD_API_DATE_FORMAT, HOLD_API_START_TIME_FORMAT } from '../constants';
import { Navigation } from './navigation';
import { TimePickerColumnHeader } from './time-picker-column-header';

const parseToEndpointDateFormat = (day: string, startTime: string) => {
  const input = `${day} ${startTime}`;
  const parsed = parse(input, `${HOLD_API_DATE_FORMAT} ${HOLD_API_START_TIME_FORMAT}`, new Date());

  const utcDate = new Date(
    Date.UTC(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate(),
      parsed.getHours(),
      parsed.getMinutes(),
      parsed.getSeconds(),
      parsed.getMilliseconds()
    )
  );

  return utcDate;
};

type SelectedSlot = {
  selectedId: string;
  selected: Timeslot | null;
};

interface TimePickerProps {
  weekdays: AppointmentSearch[];
  onNext: () => void;
  onPrev: () => void;
  onSelect: Dispatch<(Timeslot & { date: string }) | null>;
  selectedTimeslot: string;
}

export const TimePicker = ({
  selectedTimeslot,
  weekdays,
  onNext,
  onPrev,
  onSelect,
}: TimePickerProps): JSX.Element => {
  const [selected, setSelected] = useState<SelectedSlot>({
    selected: null,
    selectedId: '',
  });

  const selectSlot = useCallback(
    (x: Timeslot, result: AppointmentSearch) => (ev: React.SyntheticEvent) => {
      ev.nativeEvent.preventDefault();
      ev.nativeEvent.stopImmediatePropagation();
      setSelected({
        selectedId: x.appointmentId,
        selected: x,
      });
      const utcDate = parseToEndpointDateFormat(result.day, x.startTime);

      onSelect({
        ...x,
        date: utcDate.toISOString(),
      });
    },
    [onSelect]
  );

  const selectedTimeslotId = selected.selectedId || selectedTimeslot;

  return (
    <>
      <Navigation onNext={onNext} onPrev={onPrev} />
      {/* Todo: split days and times to get proper overflow scroll */}
      <div id="days" className={cn('d-flex justify-content-around', styles.daysWrapper)}>
        {isArray(weekdays) &&
          weekdays.map((result) => {
            return (
              <div className={CELEBRUS_CALENDAR_HEADER_CLASSNAMES} key={result.day}>
                <TimePickerColumnHeader dateString={result.day} />
              </div>
            );
          })}
      </div>
      <div className={cn('d-flex justify-content-around', styles.slotsWrapper)}>
        {isArray(weekdays) &&
          weekdays.map((result) => {
            const slots = result.timeSlots;
            return (
              <div key={result.day} className={cn(styles.timeslotsColumn)}>
                {isArray(slots) &&
                  slots.map((x: Timeslot) => {
                    // Todo extract to useCallback
                    const parsedTime = parse(x.startTime, HOLD_API_START_TIME_FORMAT, new Date());
                    // Substring cuts to show only 'a' or 'p' part of am pm. As in legacy staging.
                    const formattedTime = format(parsedTime, 'hh:mmaaa').substring(0, 6);
                    return (
                      <button
                        className={cn(styles.timeSlotBtn, {
                          [styles.timeSlotBtnSelected]: x.appointmentId === selectedTimeslotId,
                        })}
                        key={x.appointmentId}
                        onClick={selectSlot(x, result)}
                      >
                        {formattedTime}
                      </button>
                    );
                  })}
              </div>
            );
          })}
      </div>
      <div className={styles.fadeout}></div>
    </>
  );
};
